import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Sidebar } from "./AdminDashboard";
import { toast } from "sonner";
import { Search, Key, Copy, Ban, Globe, Calendar, Loader2, RefreshCw, CheckCircle, Clock, Plus } from "lucide-react";
import axios from "axios";
import { useAdminAuth, getAdminAuthHeaders } from "@/hooks/useAdminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LicenseManagement() {
  const { isLoading: authLoading, isAuthenticated, logout } = useAdminAuth();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  
  // Extend license modal state
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [extendDays, setExtendDays] = useState(5);
  const [extending, setExtending] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchLicenses();
    }
  }, [page, statusFilter, isAuthenticated]);

  const fetchLicenses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/admin/licenses?${params}`, {
        headers: getAdminAuthHeaders()
      });
      setLicenses(response.data.licenses);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error("Failed to fetch licenses:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyLicense = (key) => {
    navigator.clipboard.writeText(key);
    toast.success("License key copied!");
  };

  const revokeLicense = async (licenseKey) => {
    if (!confirm("Are you sure you want to revoke this license?")) return;
    
    try {
      await axios.put(`${API}/admin/license/${licenseKey}/revoke`, {}, {
        headers: getAdminAuthHeaders()
      });
      toast.success("License revoked successfully");
      fetchLicenses();
    } catch (error) {
      toast.error("Failed to revoke license");
    }
  };

  const reactivateLicense = async (licenseKey) => {
    try {
      await axios.post(`${API}/admin/license/${licenseKey}/reactivate`, {}, {
        headers: getAdminAuthHeaders()
      });
      toast.success("License reactivated successfully");
      fetchLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate license");
    }
  };

  const forceActivateLicense = async (licenseKey) => {
    if (!confirm("Force activate this license? This will override any current status.")) return;
    
    try {
      await axios.post(`${API}/admin/license/${licenseKey}/force-activate`, {}, {
        headers: getAdminAuthHeaders()
      });
      toast.success("License force-activated successfully");
      fetchLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to force activate license");
    }
  };

  const openExtendModal = (license) => {
    setSelectedLicense(license);
    setExtendDays(5);
    setExtendModalOpen(true);
  };

  const extendLicense = async () => {
    if (!selectedLicense) return;
    setExtending(true);
    
    try {
      const response = await axios.put(
        `${API}/admin/license/${selectedLicense.license_key}/extend`,
        { days: extendDays },
        { headers: getAdminAuthHeaders() }
      );
      toast.success(`License extended by ${extendDays} days! New expiry: ${new Date(response.data.new_expiry).toLocaleDateString()}`);
      setExtendModalOpen(false);
      fetchLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to extend license");
    } finally {
      setExtending(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-bronze" />
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700',
      pending: 'bg-amber-100 text-amber-700',
      revoked: 'bg-red-100 text-red-700',
      suspended: 'bg-orange-100 text-orange-700',
      expired: 'bg-slate-100 text-slate-700'
    };
    return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar onLogout={logout} />
      
      <main className="ml-64 p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-midnight font-outfit" data-testid="licenses-title">
            License Management
          </h1>
          <p className="text-slate-600">Manage plugin licenses and activations</p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="revoked">Revoked</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Licenses Table */}
        <Card data-testid="licenses-table">
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="spinner" />
              </div>
            ) : licenses.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Key className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No licenses found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">License Key</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Customer</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Site</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Created</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Activated</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {licenses.map((license, index) => (
                      <tr 
                        key={license.id || index} 
                        className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`license-row-${index}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Key className="w-4 h-4 text-slate-400" />
                            <code className="text-sm bg-slate-100 px-2 py-1 rounded font-mono">
                              {license.license_key}
                            </code>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => copyLicense(license.license_key)}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <p className="font-medium text-midnight">{license.customer_email}</p>
                        </td>
                        <td className="py-3 px-4">
                          {license.woocommerce_url ? (
                            <div className="flex items-center gap-1 text-sm">
                              <Globe className="w-3 h-3 text-slate-400" />
                              <span className="truncate max-w-[200px]">{license.woocommerce_url}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">Not activated</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Calendar className="w-3 h-3" />
                            {formatDate(license.created_at)}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {formatDate(license.activated_at)}
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(license.status)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1 flex-wrap">
                            {/* Extend Button - Always visible for active licenses */}
                            {license.status === 'active' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => openExtendModal(license)}
                                data-testid={`extend-btn-${index}`}
                              >
                                <Clock className="w-4 h-4 mr-1" />
                                Extend
                              </Button>
                            )}
                            {license.status === 'active' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => revokeLicense(license.license_key)}
                                data-testid={`revoke-btn-${index}`}
                              >
                                <Ban className="w-4 h-4 mr-1" />
                                Revoke
                              </Button>
                            )}
                            {(license.status === 'suspended' || license.status === 'revoked') && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  onClick={() => openExtendModal(license)}
                                  data-testid={`extend-btn-${index}`}
                                >
                                  <Clock className="w-4 h-4 mr-1" />
                                  Extend
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                  onClick={() => reactivateLicense(license.license_key)}
                                  data-testid={`reactivate-btn-${index}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Reactivate
                                </Button>
                              </>
                            )}
                            {license.status === 'pending' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => forceActivateLicense(license.license_key)}
                                data-testid={`activate-btn-${index}`}
                              >
                                <RefreshCw className="w-4 h-4 mr-1" />
                                Force Activate
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button 
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Extend License Modal */}
      <Dialog open={extendModalOpen} onOpenChange={setExtendModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="extend-license-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Extend License
            </DialogTitle>
          </DialogHeader>
          
          {selectedLicense && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">License Key</p>
                <code className="text-sm font-mono font-medium">{selectedLicense.license_key}</code>
              </div>
              
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">Customer</p>
                <p className="font-medium">{selectedLicense.customer_email}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="extend-days">Extend by (days)</Label>
                <div className="flex gap-2">
                  {[5, 7, 14, 30, 90].map((days) => (
                    <Button
                      key={days}
                      type="button"
                      variant={extendDays === days ? "default" : "outline"}
                      size="sm"
                      onClick={() => setExtendDays(days)}
                      className={extendDays === days ? "bg-blue-600 hover:bg-blue-700" : ""}
                    >
                      {days}
                    </Button>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    id="extend-days"
                    type="number"
                    min="1"
                    max="365"
                    value={extendDays}
                    onChange={(e) => setExtendDays(parseInt(e.target.value) || 1)}
                    className="w-24"
                    data-testid="extend-days-input"
                  />
                  <span className="text-sm text-slate-600">days</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={extendLicense} 
              disabled={extending}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-extend-btn"
            >
              {extending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Extending...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Extend License
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
